import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useIsMobile, { useIsSmallMobile } from '../hooks/useIsMobile';
import SEO from '../components/SEO';
import { organizationSchema } from '../utils/seoHelpers';
import HeroCarousel from '../components/HeroCarousel';
import CustomerSpotlight from '../components/CustomerSpotlight';
import CategoryRail from '../components/home/CategoryRail';
import JustArrivedShowcase from '../components/home/JustArrivedShowcase';
import CategoryShowcase from '../components/home/CategoryShowcase';
import { cldCard } from '../utils/cloudinary';
import { fetchJsonWithRetry, fetchWithRetry } from '../utils/fetchWithRetry';
import { announcementShipping, freeShippingShort, loadBusinessConfig } from '../utils/businessConfig';

const API = process.env.REACT_APP_API_URL || 'https://api.glitzandglambyjyoti.com';

const Home = () => {
  const { i18n } = useTranslation();
  const isMobile = useIsMobile();
  const isSmall = useIsSmallMobile();
  const hi = i18n.language === 'hi';
  const navigate = useNavigate();
  const S = makeS(isMobile, isSmall);
  const [featured, setFeatured] = useState([]);
  const [justArrived, setJustArrived] = useState([]);
  const [categoryRails, setCategoryRails] = useState([]); // [{ category, products: [] }]
  const [categories, setCategories] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Glitz & Glam by Jyoti — Premium Artificial Jewelry';
    // Refresh business config on mount (free shipping threshold etc) — silent fallback to defaults
    loadBusinessConfig(API).catch(() => {});
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Try the consolidated endpoint first — 1 request instead of N+1
      // (Apr28 v4: now also includes per-category rails + just arrived)
      try {
        const data = await fetchJsonWithRetry(
          `${API}/api/home-data`,
          {},
          { retries: 3, delayMs: 500 }
        );
        setFeatured(data.featured || []);
        setJustArrived(data.justArrived || []);
        setCategoryRails(data.categoryRails || []);
        setCategories(data.categories || []);
        setVideos(data.videos || []);
        return;
      } catch (consolidatedErr) {
        // Fall back to individual endpoints if /api/home-data doesn't exist yet
        // (happens if frontend is deployed before backend, or backend hasn't
        // been restarted yet to pick up the new endpoint)
        console.warn('[home] /api/home-data unavailable, falling back to individual endpoints', consolidatedErr.message);
      }

      // Fallback: parallel category-by-category fetch (less efficient but works)
      const [pRes, cRes, vRes, justRes] = await Promise.all([
        fetchWithRetry(`${API}/api/products?isFeatured=true&limit=8`, {}, { retries: 3 }),
        fetchWithRetry(`${API}/api/categories`, {}, { retries: 3 }),
        fetchWithRetry(`${API}/api/videos?limit=20`, {}, { retries: 3 }),
        fetchWithRetry(`${API}/api/products?limit=12`, {}, { retries: 3 }), // newest first
      ]);
      const pData = await pRes.json();
      const cData = await cRes.json();
      const vData = await vRes.json();
      const justData = await justRes.json();

      setFeatured(pData.products || []);
      setJustArrived(justData.products || []);
      const cats = Array.isArray(cData) ? cData : [];
      setCategories(cats);
      setVideos(vData.videos || []);

      // Fetch a rail per category in parallel (fallback path only — slow)
      const railPromises = cats.map(cat =>
        fetchWithRetry(`${API}/api/products?category=${encodeURIComponent(cat.slug || cat.name)}&limit=12`, {}, { retries: 2 })
          .then(r => r.json())
          .then(d => ({ category: cat, products: d.products || [] }))
          .catch(() => ({ category: cat, products: [] }))
      );
      const rails = await Promise.all(railPromises);
      // Hide categories that have zero products (per Vinay's spec)
      setCategoryRails(rails.filter(r => r.products.length > 0));
    } catch (e) { console.error('[home] fetchData failed:', e); }
    finally { setLoading(false); }
  };

  const CAT_ICONS = { jewelry: '💎', bangles: '💍', sarees: '🥻', hairAccessories: '🌸', bags: '👜', garments: '👗', girls: '🎀', seasonal: '🌟' };

  // Get YouTube thumbnail from videoId
  const getThumb = (v) => {
    if (v?.videoSource?.videoId && v?.videoSource?.type === 'youtube')
      return `https://img.youtube.com/vi/${v.videoSource.videoId}/mqdefault.jpg`;
    if (v?.thumbnail) return v.thumbnail;
    return null;
  };

  const FEATURES = [
    { icon: '🚚', title: hi ? 'मुफ्त शिपिंग' : 'Free Shipping', desc: freeShippingShort(hi ? 'hi' : 'en') },
    { icon: '💎', title: hi ? 'प्रीमियम क्वालिटी' : 'Premium Quality', desc: hi ? 'हर उत्पाद जाँचा हुआ' : 'Every product hand-checked' },
    { icon: '↩️', title: hi ? '7 दिन वापसी' : '7 Day Returns', desc: hi ? 'आसान रिटर्न' : 'Hassle free returns' },
    { icon: '🔒', title: hi ? 'सुरक्षित भुगतान' : 'Secure Payment', desc: hi ? 'Razorpay द्वारा' : 'Powered by Razorpay' },
  ];

  return (
    <div style={S.page}>
      <SEO
        title="Glitz & Glam by Jyoti — Premium Artificial Jewelry & Fashion"
        description="Shop artificial jewelry, bangles, hair accessories, sarees and fashion items for women. Premium quality products delivered across India. Free shipping above ₹999."
        keywords="artificial jewelry India, bangles online, kundan earrings, hair accessories, sarees, women fashion"
        url="/"
        additionalSchemas={[organizationSchema]}
      />
      <HeroCarousel isMobile={isMobile}>
        <div style={S.heroInner}>
          <div style={S.heroBadge}>✦ {hi ? 'नया कलेक्शन 2026' : 'New Collection 2026'} ✦</div>
          <h1 style={S.heroTitle}>
            {hi ? (
              <><span style={S.heroLine1}>प्रीमियम</span><em style={S.heroLine2}>कृत्रिम ज्वेलरी</em><span style={S.heroLine3}>हर अवसर के लिए</span></>
            ) : (
              <><span style={S.heroLine1}>Premium</span><em style={S.heroLine2}>Artificial Jewelry</em><span style={S.heroLine3}>For Every Occasion</span></>
            )}
          </h1>
          <p style={S.heroSub}>
            {hi ? 'प्यार से बनाई गई खूबसूरत ज्वेलरी — हर महिला के लिए' : 'Beautifully crafted with love — elegance that lasts forever'}
          </p>
          <div style={S.heroBtns}>
            <button style={S.btnPrimary} onClick={() => navigate('/shop')}>
              {hi ? 'अभी खरीदें' : 'Shop Now'} →
            </button>
            <button style={S.btnGhost} onClick={() => navigate('/shop-by-video')}>
              {hi ? 'वीडियो देखें' : 'Watch & Shop'}
            </button>
          </div>
          {/* v36 May 10 2026 — Fake hero stats deleted (500+ Products / 10K+ Happy Customers / 4.8★).
              Real numbers (~160 SKUs, no completed orders driving rating) would expose under
              India's Consumer Protection (E-Commerce) Rules 2020 §5(7). Hero is strong without them. */}
        </div>
      </HeroCarousel>

      {/* ── FEATURES STRIP ── */}
      <section style={S.featuresStrip}>
        <div style={S.featuresInner}>
          {FEATURES.map(f => (
            <div key={f.title} style={S.featureItem}>
              <span style={S.featureIcon}>{f.icon}</span>
              <div>
                <div style={S.featureTitle}>{f.title}</div>
                <div style={S.featureDesc}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CATEGORIES (Apr28 v7 — premium gallery showcase) ── */}
      <CategoryShowcase categories={categories} isMobile={isMobile} isSmall={isSmall} />

      {/* ── JUST ARRIVED — premium showcase ── */}
      {loading ? (
        <section style={{ padding: '40px 0' }}>
          <div style={{ padding: '0 32px', marginBottom: 18 }}>
            <div style={{ height: 18, width: 100, background: 'rgba(201,168,76,0.1)', borderRadius: 6, marginBottom: 10 }}/>
            <div style={{ height: 32, width: 240, background: 'rgba(255,255,255,0.04)', borderRadius: 8 }}/>
          </div>
          <div style={S.skeletonGrid}>
            {Array(6).fill(0).map((_, i) => <div key={i} style={S.skeleton}/>)}
          </div>
        </section>
      ) : (
        <JustArrivedShowcase
          products={justArrived}
          hi={hi}
          isMobile={isMobile}
          isSmall={isSmall}
        />
      )}

      {/* ── PER-CATEGORY RAILS — auto-sliding, hide if empty ── */}
      {!loading && categoryRails.map(rail => (
        <CategoryRail
          key={rail.category.slug || rail.category.name}
          category={rail.category}
          products={rail.products}
          hi={hi}
          isMobile={isMobile}
          isSmall={isSmall}
        />
      ))}

      {/* Empty state — only show if EVERYTHING is empty (no products at all) */}
      {!loading && justArrived.length === 0 && categoryRails.length === 0 && (
        <section style={S.section}>
          <div style={S.empty}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>💎</div>
            <div style={{ color: '#555', fontSize: 15 }}>{hi ? 'जल्द नए उत्पाद आएंगे!' : 'New products coming soon!'}</div>
            <button style={{ ...S.btnPrimary, marginTop: 20 }} onClick={() => navigate('/shop')}>
              {hi ? 'सभी उत्पाद देखें' : 'Browse All Products'}
            </button>
          </div>
        </section>
      )}

      {/* v36 May 10 2026 — Fake testimonials section deleted (was 6 hardcoded
          reviews with fake names: Priya Sharma / Meena Patel / Anita Singh /
          Sunita Gupta / Rekha Verma / Kavita Rao). Same legal exposure as fake
          stats: India CP (E-Commerce) Rules 2020 §5(7), fines up to ₹10 lakh.
          Real reviews are surfaced by <CustomerSpotlight /> further down the
          page (line ~440); that component returns null until ≥3 photo reviews
          exist, so the section just doesn't render until there's something
          real to show. */}

      {/* ── LATEST BLOGS ── */}
      <section style={{ ...S.section, background:'rgba(201,168,76,0.02)', padding: isMobile ? '32px 14px' : '60px 32px' }}>
        <div style={S.sectionHeader}>
          <div style={S.sectionLabel}>📝 {hi ? 'ब्लॉग' : 'Latest Blogs'}</div>
          <h2 style={S.sectionTitle}>{hi ? 'फैशन टिप्स और ट्रेंड्स' : <span>Fashion <em style={{ fontStyle:'italic', color:'#c9a84c' }}>Tips</em> & Trends</span>}</h2>
        </div>
        <LatestBlogs hi={hi} S={S} />
        <div style={{ textAlign:'center', marginTop:32 }}>
          <Link to="/blogs" style={{ ...S.btnOutline, textDecoration:'none', display:'inline-block' }}>
            {hi ? 'सभी लेख पढ़ें' : 'Read All Articles'} →
          </Link>
        </div>
      </section>

      {/* ── INSTAGRAM BANNER ── */}
      <section style={S.igBanner}>
        <div style={S.igInner}>
          {/* Left — Instagram CTA */}
          <div style={S.igLeft}>
            {/* Real Instagram gradient logo */}
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                <defs>
                  <radialGradient id="ig1" cx="30%" cy="107%" r="150%">
                    <stop offset="0%" stopColor="#fdf497"/>
                    <stop offset="5%" stopColor="#fdf497"/>
                    <stop offset="45%" stopColor="#fd5949"/>
                    <stop offset="60%" stopColor="#d6249f"/>
                    <stop offset="90%" stopColor="#285AEB"/>
                  </radialGradient>
                </defs>
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill="url(#ig1)"/>
                <circle cx="12" cy="12" r="4.5" stroke="white" strokeWidth="1.5" fill="none"/>
                <circle cx="17.5" cy="6.5" r="1" fill="white"/>
              </svg>
              <span style={{ color:'#c9a84c', fontSize:13, letterSpacing:1, fontFamily:'system-ui', fontWeight:600 }}>
                @glitz_and_glam_by_jyoti
              </span>
            </div>

            <h3 style={S.igTitle}>{hi ? 'इंस्टाग्राम पर फॉलो करें' : 'Follow Us on Instagram'}</h3>
            <p style={S.igDesc}>{hi ? 'नए कलेक्शन, ऑफर और स्टाइलिंग के लिए' : 'New collections, exclusive offers & daily styling inspiration'}</p>

            <div style={{ display:'flex', gap:10, marginTop:24, flexWrap:'wrap' }}>
              <a href="https://instagram.com/glitz_and_glam_by_jyoti" target="_blank" rel="noreferrer" style={S.igBtn}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink:0 }}>
                  <defs>
                    <radialGradient id="ig2" cx="30%" cy="107%" r="150%">
                      <stop offset="0%" stopColor="#fdf497"/>
                      <stop offset="45%" stopColor="#fd5949"/>
                      <stop offset="90%" stopColor="#285AEB"/>
                    </radialGradient>
                  </defs>
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill="url(#ig2)"/>
                  <circle cx="12" cy="12" r="4.5" stroke="white" strokeWidth="1.5" fill="none"/>
                  <circle cx="17.5" cy="6.5" r="1" fill="white"/>
                </svg>
                {hi ? 'इंस्टाग्राम फॉलो करें' : 'Follow on Instagram'}
              </a>
            </div>

            {/* v36 May 10 2026 — Fake IG stats deleted (500+ Products / 10K+ Followers / 4.8★).
                Real IG follower count is 629 per memory; showing inflated numbers is the same
                legal exposure as the deleted hero stats. Section reads cleanly without them. */}
          </div>

          {/* Right — Featured products from store (shows real products when loaded) */}
          <div style={S.igRight}>
            {featured.length > 0
              ? featured.slice(0,6).map((p,i) => (
                <a key={p._id} href={`/product/${p._id}`} style={{ ...S.igTile, textDecoration:'none', overflow:'hidden', position:'relative' }}>
                  {p.images?.[0]
                    ? <img src={cldCard(p.images[0])} alt={p.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e=>e.target.style.display='none'} loading="lazy"/>
                    : <span style={{ fontSize:28 }}>💎</span>
                  }
                  <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(0,0,0,0.5), transparent)', opacity:0, transition:'opacity 0.2s' }}
                    onMouseEnter={e=>e.currentTarget.style.opacity=1}
                    onMouseLeave={e=>e.currentTarget.style.opacity=0}>
                    <div style={{ position:'absolute', bottom:8, left:8, right:8, color:'#fff', fontSize:10, fontWeight:600 }}>
                      ₹{(p.discountPrice||p.price)?.toLocaleString()}
                    </div>
                  </div>
                </a>
              ))
              : ['💎','🌸','💍','🥻','👜','✨'].map((e,i) => (
                <div key={i} style={S.igTile}><span style={{ fontSize:28 }}>{e}</span></div>
              ))
            }
          </div>
        </div>
      </section>

      {/* ── WATCH & SHOP — Auto-scrolling Video Strip ── */}
      <section style={S.videoStrip}>
        {/* Header */}
        <div style={S.videoStripHeader}>
          <div>
            <div style={S.sectionLabel}>🎥 {hi ? 'वीडियो' : 'Watch & Shop'}</div>
            <h2 style={{ ...S.sectionTitle, margin:0 }}>
              {hi ? 'वीडियो देखें और खरीदें' : <span>Watch, Get <em>Inspired</em>, Shop</span>}
            </h2>
          </div>
          <button style={S.btnOutline} onClick={() => navigate('/shop-by-video')}>
            {hi ? 'सभी वीडियो' : 'View All Videos'} →
          </button>
        </div>

        {/* Auto-scrolling strip */}
        {videos.length > 0 ? (
          <div style={S.videoScrollWrap}>
            <style>{`
              @keyframes videoScroll {
                0%   { transform: translateX(0); }
                100% { transform: translateX(-50%); }
              }
              .video-scroll-track {
                display: flex;
                gap: 16px;
                animation: videoScroll 30s linear infinite;
                width: max-content;
              }
              .video-scroll-track:hover {
                animation-play-state: paused;
              }
              .video-card-home {
                position: relative;
                width: 220px;
                flex-shrink: 0;
                border-radius: 14px;
                overflow: hidden;
                cursor: pointer;
                border: 1px solid rgba(201,168,76,0.15);
                transition: transform 0.2s;
                background: #0e0b18;
              }
              .video-card-home:hover {
                transform: scale(1.04);
                border-color: rgba(201,168,76,0.5);
              }
            `}</style>
            <div className="video-scroll-track">
              {/* Duplicate for seamless loop */}
              {[...videos, ...videos].map((v, i) => (
                <div key={i} className="video-card-home"
                  onClick={() => navigate('/shop-by-video')}>
                  {/* Thumbnail */}
                  <div style={{ height: 160, position:'relative', overflow:'hidden', background:'#0a0810' }}>
                    {getThumb(v)
                      ? <img src={getThumb(v)} alt={v.title}
                          style={{ width:'100%', height:'100%', objectFit:'cover' }}
                          onError={e => e.target.style.display='none'}/>
                      : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:36 }}>🎬</div>
                    }
                    {/* Play button overlay */}
                    <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.3)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <div style={{ width:44, height:44, borderRadius:'50%', background:'rgba(201,168,76,0.9)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#0a0810">
                          <polygon points="5,3 19,12 5,21"/>
                        </svg>
                      </div>
                    </div>
                    {/* Source badge */}
                    <div style={{ position:'absolute', top:8, left:8, background:'rgba(0,0,0,0.7)', borderRadius:6, padding:'2px 8px', fontSize:10, color:'#fff', fontFamily:'system-ui', fontWeight:600 }}>
                      {v.videoSource?.type === 'youtube' ? '▶ YouTube' : v.videoSource?.type === 'instagram' ? '📷 Instagram' : '🎬 Video'}
                    </div>
                  </div>
                  {/* Info */}
                  <div style={{ padding:'10px 12px' }}>
                    <div style={{ color:'#e8e8f5', fontSize:12, fontWeight:600, lineHeight:1.4, marginBottom:4, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      {v.title || 'Style Video'}
                    </div>
                    {v.products?.length > 0 && (
                      <div style={{ color:'#c9a84c', fontSize:10, fontFamily:'system-ui' }}>
                        🛍️ {v.products.length} product{v.products.length > 1 ? 's' : ''} featured
                      </div>
                    )}
                    <div style={{ color:'#444', fontSize:10, marginTop:4, fontFamily:'system-ui' }}>
                      👁️ {v.views || 0} · ❤️ {v.likes || 0}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* No videos yet — show upload CTA */
          <div style={{ textAlign:'center', padding:'40px 20px', background:'rgba(201,168,76,0.04)', borderRadius:16, margin:'0 32px', border:'1px dashed rgba(201,168,76,0.2)' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>🎬</div>
            <div style={{ color:'#e8e8f5', fontSize:16, fontWeight:600, marginBottom:8 }}>
              {hi ? 'अभी कोई वीडियो नहीं' : 'No videos yet'}
            </div>
            <div style={{ color:'#555', fontSize:13, marginBottom:20 }}>
              {hi ? 'Admin → Videos में अपने YouTube/Instagram वीडियो जोड़ें' : 'Add your YouTube or Instagram videos in Admin → Videos'}
            </div>
            <button style={S.btnPrimary} onClick={() => navigate('/shop-by-video')}>
              {hi ? 'वीडियो पेज देखें' : 'Visit Shop by Video'} →
            </button>
          </div>
        )}
      </section>

      {/* Customer Spotlight — real reviews with photos (auto-hides if <3 available) */}
      <CustomerSpotlight limit={8} title={hi ? 'हमारे ग्राहक' : 'Our Customers'} />

    </div>
  );
};

const ProductCard = ({ product: p, hi, S }) => {
  const [hovered, setHovered] = useState(false);
  const discount = p.discountPrice ? Math.round((1 - p.discountPrice / p.price) * 100) : 0;

  return (
    <Link to={`/product/${p._id}`} style={{ textDecoration: 'none' }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div style={{ ...S.productCard, ...(hovered ? S.productCardHover : {}) }}>
        <div style={S.cardImgWrap}>
          {p.images?.[0]
            ? <img src={cldCard(p.images[0])} alt={p.name} style={S.cardImg} onError={e => e.target.style.display = 'none'} loading="lazy"/>
            : <span style={{ fontSize: 48 }}>💎</span>
          }
          <div style={S.cardImgOverlay}/>
          {discount > 0 && <div style={S.discountBadge}>{discount}% OFF</div>}
          {p.stock < 5 && p.stock > 0 && <div style={S.urgencyBadge}>Only {p.stock} left!</div>}
          {p.status === 'soldout' && <div style={S.soldoutOverlay}>SOLD OUT</div>}
        </div>
        <div style={S.cardBody}>
          <div style={S.cardCat}>{p.category}</div>
          <div style={S.cardName}>{hi && p.nameHi ? p.nameHi : p.name}</div>
          <div style={S.cardPriceRow}>
            <span style={S.cardPrice}>₹{(p.discountPrice || p.price).toLocaleString()}</span>
            {p.discountPrice && <span style={S.cardOldPrice}>₹{p.price.toLocaleString()}</span>}
          </div>
          {p.rating > 0 && (
            <div style={S.cardRating}>
              {'⭐'.repeat(Math.round(p.rating))} <span style={{ color: '#555', fontSize: 11 }}>({p.numReviews})</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};


const LatestBlogs = ({ hi, S }) => {
  const [blogs, setBlogs] = React.useState([]);
  const API2 = process.env.REACT_APP_API_URL || 'https://api.glitzandglambyjyoti.com';
  const CAT_COLORS = { fashion:'#818cf8', styling:'#f472b6', trends:'#34d399', jewelry:'#c9a84c', care:'#60a5fa', tips:'#fb923c' };

  React.useEffect(() => {
    fetchJsonWithRetry(`${API2}/api/blogs?limit=3`, {}, { retries: 2 })
      .then(d => setBlogs(d.blogs || []))
      .catch(() => {});
  }, []);

  if (blogs.length === 0) return (
    <div style={{ textAlign:'center', padding:'40px 20px', color:'#555' }}>
      <div style={{ fontSize:36, marginBottom:12 }}>✍️</div>
      <p>{hi ? 'अभी कोई लेख नहीं। जल्द आ रहा है!' : 'No blogs yet. Coming soon!'}</p>
    </div>
  );

  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:20 }}>
      {blogs.map(b => (
        <Link key={b._id} to={`/blog/${b._id}`} style={{ background:'#0e0b18', border:'1px solid rgba(201,168,76,0.1)', borderRadius:16, overflow:'hidden', textDecoration:'none', display:'flex', flexDirection:'column', transition:'all 0.3s' }}>
          <div style={{ height:160, background:'rgba(255,255,255,0.03)', display:'flex', alignItems:'center', justifyContent:'center', position:'relative', overflow:'hidden' }}>
            {b.featuredImage
              ? <img src={cldCard(b.featuredImage)} alt={b.title} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e=>e.target.style.display='none'} loading="lazy"/>
              : <span style={{ fontSize:32 }}>✍️</span>
            }
            <span style={{ position:'absolute', top:10, left:10, background:`${CAT_COLORS[b.category]||'#888'}22`, color:CAT_COLORS[b.category]||'#c9a84c', border:`1px solid ${CAT_COLORS[b.category]||'#888'}44`, padding:'3px 10px', borderRadius:20, fontSize:10, fontWeight:700, fontFamily:'system-ui' }}>
              {b.category}
            </span>
          </div>
          <div style={{ padding:'16px', flex:1, display:'flex', flexDirection:'column', gap:8 }}>
            <h3 style={{ color:'#e8e8f5', fontFamily:'Georgia,serif', fontSize:15, fontWeight:700, margin:0, lineHeight:1.4 }}>
              {hi && b.titleHi ? b.titleHi : b.title}
            </h3>
            {b.excerpt && <p style={{ color:'#666', fontSize:12, lineHeight:1.5, margin:0, flex:1 }}>{b.excerpt?.slice(0,90)}...</p>}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:8, borderTop:'1px solid rgba(255,255,255,0.04)' }}>
              <span style={{ color:'#555', fontSize:11 }}>✍️ {b.author||'Jyoti'} · 👁️ {b.views||0}</span>
              <span style={{ color:'#c9a84c', fontSize:11, fontWeight:600 }}>{hi?'पढ़ें →':'Read →'}</span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
};

const makeS = (isMobile, isSmall) => ({
  page: { position: 'relative', zIndex: 2 },
  hero: { minHeight: isMobile ? '70vh' : '88vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '40px 16px' : '60px 32px', textAlign: 'center', position: 'relative' },
  heroInner: { maxWidth: 700, animation: 'fadeUp 0.9s ease both', width: '100%' },
  heroBadge: { display: 'inline-block', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)', color: '#c9a84c', padding: '6px 22px', borderRadius: 24, fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', fontFamily: 'system-ui', marginBottom: 28 },
  heroTitle: { fontFamily: 'Georgia,serif', marginBottom: 20, lineHeight: 1.15 },
  heroLine1: { display: 'block', fontSize: isMobile ? 14 : 22, color: 'rgba(232,232,245,0.5)', letterSpacing: 6, textTransform: 'uppercase', fontFamily: 'system-ui', fontWeight: 300, marginBottom: 8 },
  heroLine2: { display: 'block', fontSize: isMobile ? 38 : 62, fontStyle: 'normal', fontWeight: 700, background: 'linear-gradient(135deg, #fffadc, #f0d080, #c9a84c)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' },
  heroLine3: { display: 'block', fontSize: isMobile ? 14 : 22, color: 'rgba(232,232,245,0.45)', letterSpacing: 4, textTransform: 'uppercase', fontFamily: 'system-ui', fontWeight: 300, marginTop: 8 },
  heroSub: { color: 'rgba(232,232,245,0.45)', fontSize: isMobile ? 13 : 15, letterSpacing: 1, marginBottom: 36, fontFamily: 'system-ui', fontWeight: 300 },
  heroBtns: { display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 36, flexWrap: 'wrap' },
  heroStats: { display: 'flex', gap: isMobile ? 20 : 40, justifyContent: 'center', borderTop: '1px solid rgba(201,168,76,0.1)', paddingTop: 24, flexWrap: 'wrap' },
  heroStat: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  heroStatVal: { color: '#c9a84c', fontSize: isMobile ? 18 : 22, fontWeight: 700, fontFamily: 'Georgia,serif' },
  heroStatLabel: { color: '#555568', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', fontFamily: 'system-ui' },
  btnPrimary: { background: 'linear-gradient(135deg, #c9a84c, #f0d080)', border: 'none', borderRadius: 30, padding: isMobile ? '12px 24px' : '14px 32px', color: '#04020a', fontWeight: 700, fontSize: isMobile ? 12 : 14, letterSpacing: 1, fontFamily: 'system-ui', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.25s' },
  btnGhost: { background: 'transparent', border: '1px solid rgba(201,168,76,0.4)', borderRadius: 30, padding: isMobile ? '12px 24px' : '14px 32px', color: '#c9a84c', fontWeight: 500, fontSize: isMobile ? 12 : 14, letterSpacing: 1, fontFamily: 'system-ui', textTransform: 'uppercase', cursor: 'pointer' },
  btnOutline: { background: 'transparent', border: '1px solid rgba(201,168,76,0.35)', borderRadius: 30, padding: '13px 36px', color: '#c9a84c', fontWeight: 600, fontSize: 14, letterSpacing: 1, fontFamily: 'system-ui', cursor: 'pointer' },
  featuresStrip: { background: 'rgba(201,168,76,0.04)', borderTop: '1px solid rgba(201,168,76,0.08)', borderBottom: '1px solid rgba(201,168,76,0.08)', padding: isMobile ? '16px 14px' : '20px 32px' },
  featuresInner: { maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: isMobile ? 12 : 20 },
  featureItem: { display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 14 },
  featureIcon: { fontSize: isMobile ? 20 : 26, flexShrink: 0 },
  featureTitle: { color: '#e8e8f5', fontSize: isMobile ? 11 : 13, fontWeight: 600, fontFamily: 'system-ui', marginBottom: 2 },
  featureDesc: { color: '#555568', fontSize: isMobile ? 10 : 12, fontFamily: 'system-ui' },
  section: { maxWidth: 1280, margin: '0 auto', padding: isMobile ? '32px 14px' : '60px 32px' },
  sectionHeader: { textAlign: 'center', marginBottom: 32 },
  sectionLabel: { color: 'rgba(201,168,76,0.5)', fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', fontFamily: 'system-ui', marginBottom: 10 },
  sectionTitle: { color: '#e8e8f5', fontFamily: 'Georgia,serif', fontSize: isMobile ? 22 : 32, fontWeight: 600 },
  catGrid: { display: 'grid', gridTemplateColumns: isSmall ? 'repeat(3,1fr)' : isMobile ? 'repeat(4,1fr)' : 'repeat(6,1fr)', gap: isMobile ? 8 : 14 },
  catCard: { textDecoration: 'none', borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(201,168,76,0.1)', transition: 'all 0.25s' },
  catImgWrap: { height: isMobile ? 90 : 130, background: 'rgba(255,255,255,0.03)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' },
  catImg: { width: '100%', height: '100%', objectFit: 'cover' },
  catOverlay: { position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(4,2,10,0.5), transparent)' },
  catName: { padding: isMobile ? '6px 8px' : '10px 12px', color: '#c9a84c', fontSize: isMobile ? 9 : 12, fontWeight: 600, textAlign: 'center', letterSpacing: 1, textTransform: 'uppercase', fontFamily: 'system-ui', background: '#04020a' },
  productGrid: { display: 'grid', gridTemplateColumns: isSmall ? 'repeat(2,1fr)' : isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: isMobile ? 10 : 18 },
  skeletonGrid: { display: 'grid', gridTemplateColumns: isSmall ? 'repeat(2,1fr)' : isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: isMobile ? 10 : 18 },
  skeleton: { height: isMobile ? 260 : 340, background: 'rgba(255,255,255,0.03)', borderRadius: 14, animation: 'shimmer 1.5s infinite linear', backgroundSize: '800px 100%' },
  productCard: { background: '#0e0b18', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 16, overflow: 'hidden', transition: 'all 0.3s', display: 'flex', flexDirection: 'column' },
  productCardHover: { transform: 'translateY(-5px)', borderColor: 'rgba(201,168,76,0.3)', boxShadow: '0 16px 48px rgba(0,0,0,0.4)' },
  cardImgWrap: { height: isMobile ? 160 : 200, background: 'rgba(255,255,255,0.03)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' },
  cardImg: { width: '100%', height: '100%', objectFit: 'cover' },
  cardImgOverlay: { position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(14,11,24,0.6) 0%, transparent 50%)' },
  discountBadge: { position: 'absolute', top: 10, left: 10, background: 'linear-gradient(135deg,#c9a84c,#f0d080)', color: '#04020a', fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 20, fontFamily: 'system-ui', letterSpacing: 0.5 },
  urgencyBadge: { position: 'absolute', bottom: 10, left: 10, background: 'rgba(239,68,68,0.85)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 20, fontFamily: 'system-ui' },
  soldoutOverlay: { position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 14, letterSpacing: 3, fontFamily: 'system-ui' },
  cardBody: { padding: isMobile ? '10px 12px' : '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: 4 },
  cardCat: { color: 'rgba(201,168,76,0.5)', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', fontFamily: 'system-ui' },
  cardName: { color: '#e8e8f5', fontSize: isMobile ? 12 : 14, fontWeight: 600, lineHeight: 1.4 },
  cardPriceRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 },
  cardPrice: { color: '#22c55e', fontWeight: 700, fontSize: isMobile ? 14 : 16, fontFamily: 'system-ui' },
  cardOldPrice: { color: '#444458', fontSize: 11, textDecoration: 'line-through', fontFamily: 'system-ui' },
  cardRating: { fontSize: 11, marginTop: 2 },
  empty: { textAlign: 'center', padding: '60px 0' },
  igBanner: { background: 'linear-gradient(135deg, rgba(201,168,76,0.06), rgba(201,168,76,0.02))', border: '1px solid rgba(201,168,76,0.08)', margin: isMobile ? '0 14px' : '0 32px', borderRadius: 20, padding: isMobile ? '28px 16px' : '48px 40px', position: 'relative' },
  igInner: { maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 24 : 48, alignItems: 'center' },
  igLeft: {},
  igTag: { color: '#c9a84c', fontSize: 12, letterSpacing: 2, fontFamily: 'system-ui', marginBottom: 12 },
  igTitle: { color: '#e8e8f5', fontFamily: 'Georgia,serif', fontSize: isMobile ? 22 : 28, fontWeight: 700, lineHeight: 1.3, margin: '0 0 10px' },
  igDesc: { color: '#666', fontSize: 14, fontFamily: 'system-ui', lineHeight: 1.6, margin: 0 },
  igBtn: { display:'inline-flex', alignItems:'center', gap:8, background:'linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)', border:'none', borderRadius:25, padding:'10px 20px', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', textDecoration:'none', fontFamily:'system-ui' },
  igRight: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: isMobile ? 6 : 10 },
  igTile: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow:'hidden' },
  igTitle: { color: '#e8e8f5', fontFamily: 'Georgia,serif', fontSize: 28, fontWeight: 600, marginBottom: 12 },
  igDesc: { color: '#555568', fontSize: 14, fontFamily: 'system-ui', marginBottom: 24, lineHeight: 1.6 },
  igBtn: { display: 'inline-block', background: 'linear-gradient(135deg,#c9a84c,#f0d080)', color: '#04020a', padding: '12px 28px', borderRadius: 30, fontWeight: 700, fontSize: 13, fontFamily: 'system-ui', textDecoration: 'none', letterSpacing: 0.5 },
  igRight: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 },
  igTile: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  videoStrip: { padding: isMobile ? '32px 0' : '60px 0', overflow: 'hidden' },
  videoStripHeader: { display:'flex', justifyContent:'space-between', alignItems:'flex-end', padding: isMobile ? '0 14px 20px' : '0 32px 28px', flexWrap:'wrap', gap:12 },
  videoScrollWrap: { overflow: 'hidden', paddingBottom: 12, cursor: 'grab' },
  reviewCard: { background:'#0e0b18', border:'1px solid rgba(201,168,76,0.1)', borderRadius:16, padding:'20px', display:'flex', flexDirection:'column', gap:0 },
  reviewAvatar: { width:42, height:42, borderRadius:'50%', background:'linear-gradient(135deg,#c9a84c,#f0d080)', color:'#04020a', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:16, flexShrink:0 },
  reviewText: { color:'#9090aa', fontSize:13, lineHeight:1.7, fontStyle:'italic', margin:'0 0 10px' },
  reviewItem: { color:'rgba(201,168,76,0.6)', fontSize:11, fontFamily:'system-ui', fontWeight:600 },



});

export default Home;
